import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { format } from 'date-fns';
import SyncIcon from '@mui/icons-material/Sync';
import api, { DAILY_PNL } from '../../../infrastructure/config/api';
import { vi } from 'date-fns/locale';

// Tạo danh sách tháng/năm cho dropdown (từ tháng 10/2025 đến hiện tại)
const generateMonthYearOptions = () => {
  const months = [];
  const today = new Date();
  const startDate = new Date(2025, 9, 1); // Tháng 10/2025 (tháng 9 vì index bắt đầu từ 0)

  // Tạo danh sách từ tháng hiện tại về tháng 10/2025
  let currentDate = new Date(today.getFullYear(), today.getMonth(), 1);

  while (currentDate >= startDate) {
    months.push(new Date(currentDate));
    currentDate.setMonth(currentDate.getMonth() - 1);
  }

  // Format thành MM/yyyy và tên tháng
  return months.map(date => ({
    value: format(date, 'MM/yyyy'),
    label: format(date, 'MMMM yyyy', { locale: vi })
  }));
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  return parseFloat(num).toFixed(decimals);
};

const DailyPnlList = () => {
  const [dailyPnl, setDailyPnl] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthYear, setMonthYear] = useState(() => {
    const today = new Date();
    return format(today, 'MM/yyyy');
  });
  const [monthYearOptions] = useState(generateMonthYearOptions());
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const theme = useTheme();
  const navigate = useNavigate();

  const fetchDailyPnl = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching daily PnL data...');

      // Parse selected month/year and calculate date range
      const [month, year] = monthYear.split('/').map(Number);
      const startDate = format(new Date(year, month - 1, 1), 'yyyy-MM-dd');
      const endDate = format(new Date(year, month, 0), 'yyyy-MM-dd');

      const response = await api.get(DAILY_PNL.DETAILED(startDate, endDate));
      console.log('API URL:', response.config.url);

      console.log('API Response status:', response.status);
      console.log('API Response headers:', response.headers);
      console.log('API Response data structure:', Object.keys(response.data));

      // Handle the API response format
      const responseData = response.data;
      console.log('Raw response data:', JSON.stringify(responseData, null, 2));

      // Log the first few items for debugging
      if (responseData?.data?.length > 0) {
        console.log('First 3 items in response data:', responseData.data.slice(0, 3));
      }

      // Extract the data array from the response
      let pnlData = [];
      if (responseData && responseData.success && responseData.data) {
        pnlData = Array.isArray(responseData.data) ? responseData.data : [responseData.data];
      } else if (Array.isArray(responseData)) {
        pnlData = responseData;
      }

      // Sort by date in descending order (newest first)
      const sortedPnlData = [...pnlData].sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : 0;
        const dateB = b.date ? new Date(b.date) : 0;
        console.log(`Sorting: ${a.date} (${dateA}) vs ${b.date} (${dateB})`);
        return dateB - dateA; // Descending order
      });

      console.log('Sorted PnL data (first 5):', JSON.stringify(sortedPnlData.slice(0, 5), null, 2));

      console.log('Processed PnL data:', sortedPnlData);
      console.log('Daily PnL count:', sortedPnlData.length);
      setDailyPnl(sortedPnlData);
    } catch (error) {
      console.error('Error fetching PnL data:', error);
      setDailyPnl([]);
    } finally {
      setLoading(false);
    }
  }, [monthYear]);

  // Reset to first page when data changes
  useEffect(() => {
    setPage(1);
  }, [dailyPnl]);

  // Calculate pagination
  const totalItems = dailyPnl?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedItems = dailyPnl?.slice(startIndex, startIndex + itemsPerPage) || [];

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch data when month/year changes
  useEffect(() => {
    try {
      console.log('dailyPnl state updated:', {
        count: dailyPnl?.length,
        hasData: dailyPnl && dailyPnl.length > 0,
        sampleItem: dailyPnl?.[0] || 'No items',
        isLoading: loading
      });
    } catch (error) {
      console.error('Error in dailyPnl effect:', error);
    }
  }, [dailyPnl, loading]);

  // Initial fetch and refetch when month/year changes
  useEffect(() => {
    console.log('Fetching PnL data for month/year:', monthYear);

    const fetchData = async () => {
      try {
        await fetchDailyPnl();
      } catch (error) {
        console.error('Failed to fetch PnL data:', error);
      }
    };

    fetchData();
  }, [fetchDailyPnl, monthYear]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Format the selected month/year for display
  const selectedMonthYear = () => {
    const [month, year] = monthYear.split('/');
    return `Tháng ${parseInt(month)}/${year}`;
  };

  return (
    <Box sx={{ width: '100%', px: 2, mx: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'space-between' }, // Center on xs, space-between on sm+
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' }, // Allow wrapping on xs
          mb: 2
        }}>
          <Typography variant="h5" component="h1" sx={{ mb: { xs: 1, sm: 0 } }}>Lợi nhuận theo tháng</Typography>
          <Box sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: { xs: 'wrap', sm: 'nowrap' }, // Allow wrapping on extra-small screens
            justifyContent: { xs: 'center', sm: 'flex-start' }, // Center items on extra-small screens
          }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel id="month-year-select-label">Chọn tháng/năm</InputLabel>
              <Select
                labelId="month-year-select-label"
                id="month-year-select"
                value={monthYear}
                label="Chọn tháng/năm"
                onChange={(e) => setMonthYear(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                    borderRadius: 1,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                {monthYearOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={fetchDailyPnl}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {loading ? 'Đang tải...' : 'Tải lại'}
            </Button>
          </Box>
        </Box>

        {/* Total PnL Summary */}
        <Box sx={{ mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" color="text.secondary">Tổng lợi nhuận</Typography>
                  <Typography variant="h4" sx={{
                    fontWeight: 'bold',
                    color: dailyPnl?.reduce((sum, item) => sum + (parseFloat(item?.totalPnl) || 0), 0) >= 0 ? 'success.main' : 'error.main'
                  }}>
                    {dailyPnl?.reduce((sum, item) => {
                      const value = parseFloat(item?.totalPnl) || 0;
                      return sum + value;
                    }, 0).toFixed(2)} USDT
                  </Typography>
                </Box>
                <Chip
                  label={dailyPnl?.reduce((sum, item) => sum + (parseFloat(item?.totalPnl) || 0), 0) >= 0 ? 'LÃI' : 'LỖ'}
                  color={dailyPnl?.reduce((sum, item) => sum + (parseFloat(item?.totalPnl) || 0), 0) >= 0 ? 'success' : 'error'}
                  sx={{ fontWeight: 'bold', fontSize: '1rem', px: 2, py: 1 }}
                />
              </Box>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Số giao dịch: {dailyPnl?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedMonthYear()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Daily PnL Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)'
        },
        gap: 2,
        mb: 3
      }}>
        {paginatedItems && paginatedItems.length > 0 ? (
          paginatedItems.map((item, index) => {
            if (!item) {
              console.warn('Undefined item at index:', index);
              return null;
            }

            console.log('Rendering item:', item);
            const pnlValue = parseFloat(item.totalPnl) || 0;
            const pnlColor = pnlValue >= 0 ? 'success.main' : 'error.main';
            const pnlSign = pnlValue >= 0 ? '+' : '';

            return (
              <Card
                key={item.id}
                variant="outlined"
                onClick={() => navigate(`/position-history/${item.symbol}`)}
                sx={{
                  borderLeft: `4px solid ${theme.palette[pnlValue >= 0 ? 'success' : 'error'].main}`,
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" component="div" fontWeight="bold">
                      {item.symbol || 'N/A'}
                    </Typography>
                    <Chip
                      label={pnlValue >= 0 ? 'Lãi' : 'Lỗ'}
                      color={pnlValue >= 0 ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Ngày: {item.date ? format(new Date(item.date), 'dd/MM/yyyy') : 'N/A'}
                    </Typography>
                    <Typography
                      variant="h5"
                      color={pnlColor}
                      sx={{ mt: 1, fontWeight: 'bold' }}
                    >
                      {`${pnlSign}${formatNumber(pnlValue)} USDT`}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
                    <Typography variant="caption" color="text.secondary">
                      Cập nhật: {item.updatedAt ? format(new Date(item.updatedAt), 'HH:mm dd/MM/yyyy') : 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Box sx={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            p: 4,
            border: '1px dashed #ddd',
            borderRadius: 1
          }}>
            <Typography variant="body1" color="text.secondary">
              Không có dữ liệu lợi nhuận nào trong tháng {selectedMonthYear()}
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchDailyPnl}
              disabled={loading}
              startIcon={<SyncIcon />}
              sx={{ mt: 2 }}
            >
              Thử lại
            </Button>
          </Box>
        )}
      </Box>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="large"
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '1rem',
                  minWidth: '40px',
                  height: '40px',
                  '&.Mui-selected': {
                    fontWeight: 'bold',
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              }}
            />
            <Box sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
              Hiển thị {Math.min(startIndex + 1, totalItems)} - {Math.min(startIndex + itemsPerPage, totalItems)} trên tổng số {totalItems} kết quả
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default DailyPnlList;
